// src/modules/app-views/customers/components/EditEntityModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useRef } from 'react';
import { Alert } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import SimpleEntityForm from 'components/SimpleComponents/SimpleEntityForm/SimpleEntityForm';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import { returnOnlyNumbers } from 'utils/helpers';
import translateX from 'utils/translateX';

// *******************************************************************************
// FORM POPULATOR WRAPPER
// *******************************************************************************
// Wrapper component that populates form with fetched data
// This is necessary because initialValues only works on mount
const FormPopulator = ({ entityData, children }) => {
  const { form, setFormCnpjState, setFormCpfState } = useSimpleFormContext();
  const hasPopulatedRef = useRef(false);
  const lastEntityIdRef = useRef(null);

  useEffect(() => {
    // Only populate if we have new entity data and form is available
    if (!form || !entityData?._id) return;

    // Skip if we already populated for this entity
    if (hasPopulatedRef.current && lastEntityIdRef.current === entityData._id) return;

    // Populate form with fetched data
    form.setFieldsValue(entityData);

    // Set lastSearchFound for CNPJ/CPF to prevent unnecessary API searches on blur
    const cnpjValue = entityData.cnpj?.value || entityData.cnpj;
    const cpfValue = entityData.cpf?.value || entityData.cpf;

    if (cnpjValue) {
      const cleanCnpj = returnOnlyNumbers(cnpjValue);
      if (cleanCnpj?.length === 14) {
        // Use the field name for CNPJ (usually 'cnpj.value')
        setFormCnpjState?.('cnpj.value', { lastSearchFound: cleanCnpj });
      }
    }

    if (cpfValue) {
      const cleanCpf = returnOnlyNumbers(cpfValue);
      if (cleanCpf?.length === 11) {
        setFormCpfState?.('cpf.value', { lastSearchFound: cleanCpf });
      }
    }

    // Mark as populated for this entity
    hasPopulatedRef.current = true;
    lastEntityIdRef.current = entityData._id;
  }, [form, entityData, setFormCnpjState, setFormCpfState]);

  return children;
};

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const Step1Content = ({ entityState, setEntityState, defaultEntityProfiles, entityData }) => {
  // Check if entity was enriched via API
  const isEnrichedAndPersisted = entityData?._id && entityData?.dataEnrichment?.isEnriched === true;

  return (
    <FormPopulator entityData={entityData}>
      <SimpleEntityForm
        entityTypeModel="manual"
        locale="BR"
        setState={setEntityState}
        state={entityState}
        showAddressForm={true}
        // Form selectors configuration
        isEntityProfile={true}
        dataOrigin="new_data"
        hideFormCountrySelector={false}
        hideDataOriginSelector={true}
        hideEntityTypeSelector={false}
        hideEntityProfileSelector={false}
        defaultEntityProfiles={defaultEntityProfiles}
        hideAddressCountrySelector={true}
        // CNPJ/CPF search configuration
        unique={true} // Search internal database for existing entity
        searchCnpjByApi={true} // Search external API if not found internally
        searchCpfByApi={true} // Search external API for CPF too
        useInModal={true}
        isEnrichedAndPersisted={isEnrichedAndPersisted}
      />

      {/* Show info alert when entity was enriched via API */}
      {/*{isEnrichedAndPersisted && (*/}
      {/*  <Alert*/}
      {/*    type="info"*/}
      {/*    showIcon*/}
      {/*    icon={<InfoCircleOutlined />}*/}
      {/*    title={translateX('entity_enriched_via_api')}*/}
      {/*    style={{ width: '100%', marginTop: 16 }}*/}
      {/*  />*/}
      {/*)}*/}
    </FormPopulator>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const editEntityStep1 = ({
  entityState,
  setEntityState,
  defaultEntityProfiles = ['customer'],
  entityData = null,
}) => ({
  stepTitle: 'entity_data',
  // NOTE: validationFields removed - validation is handled by requiredFields prop
  // which supports 'uniqueKey' alias and custom error handling via addFieldsWithError
  content: () => (
    <Step1Content
      entityState={entityState}
      setEntityState={setEntityState}
      defaultEntityProfiles={defaultEntityProfiles}
      entityData={entityData}
    />
  ),
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default editEntityStep1;
